import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NON_SUPPLIER_ROLES } from "src/constants/permissions";
import { useAppDispatch } from "src/redux/hooks";
import { fetchTerritories } from "src/redux/slices/territories/territorySlice";

import { useMutateError } from "@features/errors";
import { Territory } from "@models/Territory";
import client, { RequestParams } from "@services/api";
import { QueryOptions } from "@utils/reactQuery";
import useRoleIs from "@utils/useRoleIs";

export type TerritoryListFilter = {
  isAwaitingCode?: boolean;
  isActive?: boolean;
};

export const territoriesKeyFactory = createQueryKeys("territories", {
  list: (params?: RequestParams) => ({
    queryKey: [params],
    queryFn: () =>
      client
        .get<Territory[]>("territories", { params })
        .then((res) => res.data),
  }),
});

export const useTerritories = (
  params: RequestParams = {},
  options?: QueryOptions<Territory[]>
) => {
  const roleIs = useRoleIs();
  return useQuery({
    ...territoriesKeyFactory.list(params),
    staleTime: Infinity,
    enabled: roleIs(NON_SUPPLIER_ROLES),
    ...options,
  });
};

export const useTerritoriesQuery = (
  params: RequestParams,
  options?: QueryOptions<Territory[]>
) => {
  return useQuery({
    ...territoriesKeyFactory.list(params),
    staleTime: Infinity,
    ...options,
  });
};

type TerritoryMutationAttributes = {
  name: string;
  externalCode: string;
  externalBillingId: string | null;
  externalAnaplanId: string | null;
  exemptFromBudgeting: boolean;
  states: { type: "state"; id: string }[];
  salesGroup: { type: "sales-group"; id: string } | null;
};

export type CreateTerritoryPayload = TerritoryMutationAttributes & {
  type: Territory["type"];
};

export type UpdateTerritoryPayload = TerritoryMutationAttributes & {
  id: string;
};

const invalidateTerritories = (
  queryClient: ReturnType<typeof useQueryClient>,
  dispatch: ReturnType<typeof useAppDispatch>
) => {
  queryClient.invalidateQueries({
    queryKey: territoriesKeyFactory.list._def,
  });
  void dispatch(fetchTerritories() as never);
};

export const useCreateTerritoryMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const setError = useMutateError();

  return useMutation({
    mutationFn: (payload: CreateTerritoryPayload) =>
      client
        .post<Territory>("territories", {
          ...payload,
          __type: "territory",
          relationshipNames: ["states", "salesGroup"],
        })
        .then((res) => res.data),
    onSuccess: () => invalidateTerritories(queryClient, dispatch),
    onError: (error) => setError(error, "Create Territory"),
  });
};

export const useUpdateTerritoryMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const setError = useMutateError();

  return useMutation({
    mutationFn: ({ id, ...payload }: UpdateTerritoryPayload) =>
      client
        .update<Territory>(`territories/${id}`, {
          ...payload,
          __type: "territory",
          relationshipNames: ["states", "salesGroup"],
        })
        .then((res) => res.data),
    onSuccess: () => invalidateTerritories(queryClient, dispatch),
    onError: (error) => setError(error, "Update Territory"),
  });
};
